import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@store/theme/common/design";
import { GalleryPhotosModel } from "@store/Widgets/ImageGallery/ImageGalleryStore";
import Color from "color";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useCallback, useEffect } from "react";
import { css } from "styled-components";

interface OwnProps {
    rawDataCollection: any;
    imageCollection: GalleryPhotosModel[];
    setSlideIndex: (slideIndex: number) => void;
    slideIndex: number;
}

export interface SlideshowImageGalleryStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    arrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideWrapperStyles?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
}

const arrowWrapperStyles = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        margin-top: -20px;
        background-color: ${Color(COLORS.secondary.base).rgb().alpha(0.6).string()};
    `,
};

export const slideshowImageGalleryStyles: SlideshowImageGalleryStyles = {
    slideshowWrapper: {
        css: css`
            padding: 10px 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        ...arrowWrapperStyles,
    },
    nextArrowWrapper: {
        css: css`
            ${arrowWrapperStyles?.css}
            right: 0;
        `,
    },
    arrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideWrapperStyles: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -10px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            min-height: 0;
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${COLORS.border.base};
            &:hover,
            &:active {
                background: ${COLORS.border.base};
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const styles = slideshowImageGalleryStyles;

const SlideshowImageGallery = ({ rawDataCollection, imageCollection, setSlideIndex, slideIndex }: OwnProps) => {
    const [emblaRef, embla] = useEmblaCarousel();

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSlideIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
            startIndex: slideIndex,
        });
    }, [embla, rawDataCollection]);

    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            {imageCollection.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button {...styles.arrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>
                    {imageCollection.map(({ largeImagePath, id, altText }) => {
                        return (
                            <StyledWrapper key={`modal_${id}`} {...styles.slideWrapperStyles}>
                                <LazyImage src={largeImagePath} altText={altText} />
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
            {imageCollection.length > 1 && (
                <>
                    {imageCollection.length < 19 && (
                        <StyledWrapper {...styles.dotsContainerWrapper}>
                            {imageCollection.map((_, i) => {
                                const dotColorStyles =
                                    i === slideIndex
                                        ? `
                                            background: ${COLORS.primary.base};
                                            &:hover { background: ${COLORS.primary.base}; }
                                        `
                                        : "";
                                const dotButtonStyles = {
                                    css: css`
                                        ${styles.dotButton?.css || ""}
                                        ${dotColorStyles}
                                    `,
                                };
                                return (
                                    <Button
                                        key={`dot_${i}`}
                                        {...dotButtonStyles}
                                        onClick={() => embla && embla.scrollTo(i)}
                                    />
                                );
                            })}
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.nextArrowWrapper}>
                        <Button {...styles.arrowButton} onClick={() => embla && scrollNext()}>
                            <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

export default SlideshowImageGallery;
